import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * The app's one button. Pass `to` and it renders a router <Link> (so it
 * navigates without a reload and still opens in a new tab on middle-click);
 * otherwise it is a real <button>.
 *
 *   primary   - the one filled blue action
 *   secondary - an outlined action that sits quietly inside a panel
 *
 * Colours come from the --dash-* tokens in index.css, which are defined
 * globally for both themes, so it reads correctly in light and dark.
 */

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md';
  /** Router path. When set the button renders as a link. */
  to?: string;
  className?: string;
  children: ReactNode;
}

const VARIANT = {
  primary: 'bg-dash-action text-white hover:bg-dash-action-hover',
  secondary:
    'border border-dash-line-strong bg-transparent text-dash-ink hover:bg-dash-row',
} as const;

const SIZE = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-9 px-4 text-[13px]',
} as const;

export function Button({
  variant = 'secondary',
  size = 'sm',
  to,
  className = '',
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const cls = `dash-focus inline-flex shrink-0 items-center justify-center rounded-lg font-semibold transition-colors disabled:pointer-events-none disabled:opacity-40 ${VARIANT[variant]} ${SIZE[size]} ${className}`;
  if (to) return <Link to={to} className={cls}>{children}</Link>;
  return <button type={type} className={cls} {...rest}>{children}</button>;
}
